// src/components/patient/booking/bookingUi.jsx
//
// Presentation shared by the booking page, the booking panel and the
// hold/book step. Kept in the look the existing /patient/book-appointment
// page already used.

import React from "react";

export function classNames(...values) {
  return values.filter(Boolean).join(" ");
}

export function Icon({ name, className = "h-5 w-5" }) {
  switch (name) {
    case "search":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2.2" />
          <path d="M20 20l-4-4" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
        </svg>
      );
    case "map":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M9 18l-5 2V6l5-2m0 14 6-2m-6 2V4m6 12 5 2V4l-5-2m0 14V2" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "video":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <rect x="3" y="7" width="12" height="10" rx="2" stroke="currentColor" strokeWidth="2.1" />
          <path d="m15 11 5-3v8l-5-3" stroke="currentColor" strokeWidth="2.1" strokeLinejoin="round" />
        </svg>
      );
    case "credit":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <rect x="3" y="5" width="18" height="14" rx="3" stroke="currentColor" strokeWidth="2.1" />
          <path d="M3 10h18M7 15h4" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" />
        </svg>
      );
    case "star":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M12 2.7l2.83 5.73 6.32.92-4.57 4.45 1.08 6.29L12 17.12 6.34 20.09l1.08-6.29-4.57-4.45 6.32-.92L12 2.7z" />
        </svg>
      );
    case "check":
      return (
        <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="m5 13 4 4L19 7" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    default:
      return null;
  }
}

const TONES = {
  neutral: "bg-slate-50 text-slate-700 border-slate-200",
  info: "bg-sky-50 text-sky-700 border-sky-200",
  success: "bg-emerald-50 text-emerald-700 border-emerald-200",
  warn: "bg-amber-50 text-amber-700 border-amber-200",
  violet: "bg-violet-50 text-violet-700 border-violet-200",
  danger: "bg-rose-50 text-rose-700 border-rose-200",
};

export function Pill({ children, tone = "neutral" }) {
  return (
    <span className={classNames("inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold", TONES[tone] || TONES.neutral)}>
      {children}
    </span>
  );
}

export function RatingBadge({ rating, count, fallback = "New service" }) {
  const numericRating = Number(rating || 0);
  const numericCount = Number(count || 0);
  if (!numericRating || !numericCount) return <Pill tone="neutral">{fallback}</Pill>;
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-800">
      <Icon name="star" className="h-3.5 w-3.5 text-amber-500" />
      {numericRating.toFixed(1)} · {numericCount} review{numericCount === 1 ? "" : "s"}
    </span>
  );
}

export function money(amount, currency) {
  if (amount === null || amount === undefined || amount === "") return "—";
  if (!currency) return String(amount);
  try {
    return new Intl.NumberFormat("en-CA", { style: "currency", currency, maximumFractionDigits: 2 }).format(Number(amount));
  } catch {
    return `${currency} ${amount}`;
  }
}

export const MODE_LABELS = { in_clinic: "In-Clinic", video: "Video" };

export function modeLabel(mode) {
  return MODE_LABELS[mode] || "";
}

export function modeTone(mode) {
  if (mode === "video") return "violet";
  if (mode === "in_clinic") return "info";
  return "neutral";
}

export function TimezoneNotice({ timeZone, className = "" }) {
  return (
    <p className={classNames("text-xs font-semibold text-slate-600", className)} data-testid="timezone-notice">
      Times shown in {timeZone || "the server's default time zone"}
    </p>
  );
}

export function InlineError({ message, onRetry, retryLabel = "Retry" }) {
  if (!message) return null;
  return (
    <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
      <span>{message}</span>
      {onRetry ? (
        <button type="button" onClick={onRetry} className="rounded-2xl border border-rose-300 bg-white px-3 py-1.5 text-sm font-semibold text-rose-700 hover:bg-rose-100">
          {retryLabel}
        </button>
      ) : null}
    </div>
  );
}
